import { useCallback, useEffect, useMemo, useState } from "react";
import adminApi from "../api/adminApi";

function clampInt(value, min, max, fallback) {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, Math.floor(n)));
}

export function useAdminAiOffers({ adminToken, enabled }) {
  const [query, setQuery] = useState("");
  const [enabledFilter, setEnabledFilter] = useState("all"); // all|enabled|disabled
  const [orderBy, setOrderBy] = useState("updated_at");
  const [orderDir, setOrderDir] = useState("DESC");
  const [limit, setLimit] = useState(25);
  const [offset, setOffset] = useState(0);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    if (!adminToken) return;
    setLoading(true);
    setError(null);
    try {
      const enabledParam =
        enabledFilter === "enabled" ? true : enabledFilter === "disabled" ? false : undefined;
      const res = await adminApi.listAiOffers(adminToken, {
        q: query || "",
        enabled: enabledParam,
        limit,
        offset,
        orderBy,
        orderDir
      });
      setItems(Array.isArray(res.data?.items) ? res.data.items : []);
      setTotal(Number(res.data?.total || 0));
    } catch (e) {
      console.error("AI offers load error:", e);
      setError(e?.response?.data?.error || e?.message || "Ошибка загрузки");
    } finally {
      setLoading(false);
    }
  }, [adminToken, enabledFilter, limit, offset, orderBy, orderDir, query]);

  useEffect(() => {
    if (!enabled) return;
    reload();
  }, [enabled, reload]);

  const page = useMemo(() => Math.floor(offset / limit) + 1, [offset, limit]);
  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / limit)), [total, limit]);

  const setPage = useCallback((nextPage) => {
    const p = clampInt(nextPage, 1, 1_000_000, 1);
    setOffset((p - 1) * limit);
  }, [limit]);

  const createOffer = useCallback(async (payload) => {
    const res = await adminApi.createAiOffer(adminToken, payload);
    await reload();
    return { offer: res.data?.offer || null, warnings: res.data?.warnings || [] };
  }, [adminToken, reload]);

  const updateOffer = useCallback(async (id, payload) => {
    const res = await adminApi.updateAiOffer(adminToken, id, payload);
    await reload();
    return { offer: res.data?.offer || null, warnings: res.data?.warnings || [] };
  }, [adminToken, reload]);

  const deleteOffer = useCallback(async (id) => {
    await adminApi.deleteAiOffer(adminToken, id);
    await reload();
  }, [adminToken, reload]);

  const bulkAction = useCallback(async (action, ids) => {
    await adminApi.bulkAiOffers(adminToken, action, ids);
    await reload();
  }, [adminToken, reload]);

  const previewSimilarity = useCallback(async (offerId, text) => {
    const res = await adminApi.previewAiOfferSimilarity(adminToken, offerId, text);
    return res.data || null;
  }, [adminToken]);

  const validateOffer = useCallback(async (payload) => {
    const res = await adminApi.validateAiOffer(adminToken, payload);
    return res.data || null;
  }, [adminToken]);

  const debugRank = useCallback(async (params) => {
    const res = await adminApi.debugRankAiOffers(adminToken, params || {});
    return res.data || null;
  }, [adminToken]);

  const debugRuntime = useCallback(async (params) => {
    const res = await adminApi.debugRuntimeAiOffers(adminToken, params || {});
    return res.data || null;
  }, [adminToken]);

  const loadTimeseries = useCallback(async (offerId, days = 30) => {
    const res = await adminApi.getAiOfferTimeseries(adminToken, offerId, days);
    return res.data?.items || [];
  }, [adminToken]);

  return {
    state: {
      query,
      enabledFilter,
      orderBy,
      orderDir,
      limit,
      offset,
      items,
      total,
      loading,
      error,
      page,
      totalPages
    },
    actions: {
      setQuery,
      setEnabledFilter,
      setOrderBy,
      setOrderDir,
      setLimit,
      setOffset,
      setPage,
      reload,
      createOffer,
      updateOffer,
      deleteOffer,
      bulkAction,
      previewSimilarity
      ,
      validateOffer,
      debugRank,
      debugRuntime,
      loadTimeseries
    }
  };
}

export default useAdminAiOffers;
